import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'antd'

import B2becButton from '@/components/B2becButton'
import B2becQuantity from '@/components/B2becQuantity'
import { ChevronLeftIcon } from '@/components/Icons'
import { NotificationManager } from '@/components/Notification'
import {
  useAddProductToWishlist,
  useCreateAndAddProductToWishlist,
  useFetchWishlists,
} from '@/pages/WishlistOverview/useFetchWishlist'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectIsAddProductToWishlistModalVisible,
  selectProductDetail,
} from '@/store/selectors/wishlistSelectors'
import { selectUserId } from '@/store/slices/authenticationSlice'
import { clearProductDetail, hideAddProductToWishlistModal } from '@/store/slices/wishlistSlice'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import BREAKPOINTS from '@/libs/constants/breakpoints'
import { MODULE_WISHLIST_RIGHTS } from '@/libs/constants/modulerights'
import { isValidArray } from '@/libs/utils/array'
import { getErrorData, getErrorStatus } from '@/libs/utils/errorUtils'

import { NewWishlistNameInput } from '../NewWishlistNameInput'
import styles from './AddProductToWishlistModal.module.scss'
import { WishlistItem } from './WishlistItem'

const skeletons = Array.from({ length: 3 })
  .fill(0)
  .map((_, index) => <WishlistItem.Loading key={index} />)

interface ProductToBeAddedToWishlistProps {
  materialName: string
  materialNumber: string
  img: string
  quantity: number
  onChangeQuantity: (newQuantity: number) => void
}

function ProductToBeAddedToWishlist({
  materialName = '',
  materialNumber = '',
  img = '',
  quantity = 1,
  onChangeQuantity,
}: ProductToBeAddedToWishlistProps) {
  return (
    <div className={styles.product}>
      <div className={styles.image}>
        <img alt={materialName} src={img} />
      </div>
      <div className={styles.content}>
        <span className={styles['material-name']}>{materialName}</span>
        <span className={styles['sub-information']}>{materialNumber}</span>
      </div>
      <div className={styles.action}>
        <B2becQuantity
          initialQuantity={quantity}
          inputClassName={styles.quantity}
          minIntDigits={1}
          onChange={onChangeQuantity}
        />
      </div>
    </div>
  )
}

interface ConfirmationModalProps {
  isExecuting: boolean
  open: boolean
  onOk: () => void
  onCancel: () => void
}

function ConfirmationModal({
  isExecuting,
  open,
  onOk,
  onCancel,
}: ConfirmationModalProps) {
  const { t } = useTranslation()

  return (
    <Modal
      closable={false}
      open={open}
      title={t('wishlist.modal.addProduct.confirmTitle')}
      footer={[
        <B2becButton
          key="confirmation-modal-cancel-button"
          ghost
          className={styles['confirmation-modal__button']}
          disabled={isExecuting}
          onClick={onCancel}
        >
          {t('buttonTexts.cancel')}
        </B2becButton>,
        <B2becButton
          key="confirmation-modal-ok-button"
          className={styles['confirmation-modal__button']}
          loading={isExecuting}
          onClick={onOk}
        >
          {t('buttonTexts.save')}
        </B2becButton>,
      ]}
      onCancel={onCancel}
      onOk={onOk}
    >
      <p>{t('wishlist.modal.addProduct.confirmContent')}</p>
    </Modal>
  )
}

export default function AddProductToWishListModal() {
  // Remove this ref when upgrading to React 18
  const isMountedRef = useRef(true)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { mutateAsync: addProductToWishlist } = useAddProductToWishlist()
  const { mutateAsync: createAndAddProductToWishlist } = useCreateAndAddProductToWishlist()
  const isLargerThan992 = useMediaQuery(`(min-width:${BREAKPOINTS.lg}px)`)
  const isAddProductToWishlistModalVisible = useAppSelector(
    selectIsAddProductToWishlistModalVisible,
  )
  const { materialName, materialNumber, img } = useAppSelector(selectProductDetail)
  const [newWishlistName, setNewWishlistName] = useState('')
  const [shouldShowConfirmModal, setShouldShowConfirmModal] = useState(false)
  const [isExecuting, setIsExecuting] = useState(false)
  const [loadingIds, setLoadingIds] = useState({})
  const [quantity, setQuantity] = useState(1)
  const canSave = newWishlistName.trim().length > 0
  const userId = useAppSelector(selectUserId)

  const { fetchOwnWishlists } = useFetchWishlists()
  const { data: wishlistsData, isFetching: isFetchingWishlists } = fetchOwnWishlists
  const wishlists = wishlistsData?.items ?? []
  const isFetchWishlistsSuccess = !isFetchingWishlists && wishlistsData

  const handleChangeQuantity = (newQuantity) => {
    setQuantity(newQuantity)
  }

  const handleChangeNewWishlistName = (e) => {
    setNewWishlistName(e.target.value)
  }

  const handleCancel = () => {
    dispatch(clearProductDetail())
    dispatch(hideAddProductToWishlistModal())
  }

  const handleSave = async () => {
    if (canSave) {
      try {
        setIsExecuting(true)

        await createAndAddProductToWishlist({
          wishlistName: newWishlistName,
          ownerId: userId,
          materialName,
          materialNumber,
          quantity,
        })

        NotificationManager.success({
          message: 'notification.success.addProductToWishlist',
        })

        dispatch(clearProductDetail())
        dispatch(hideAddProductToWishlistModal())
      } catch (error) {
        NotificationManager.error({
          message: 'notification.error.createWishlist',
        })
        console.error('failed to create new wishlist', error)
      } finally {
        if (isMountedRef.current) {
          setShouldShowConfirmModal(false)
          setIsExecuting(false)
        }
      }
    }
  }

  const handleAdd = async ({ ownerId, wishlistId }) => {
    setLoadingIds((previous) => ({
      ...previous,
      [wishlistId]: true,
    }))

    try {
      await addProductToWishlist({
        ownerId,
        wishlistId,
        materialName,
        materialNumber,
        quantity,
      })

      NotificationManager.success({
        message: 'notification.success.addProductToWishlist',
      })

      dispatch(clearProductDetail())
      dispatch(hideAddProductToWishlistModal())
    } catch (error: any) {
      const status = getErrorStatus(error)
      const data = getErrorData(error)

      let message = 'notification.error.addProductToWishlist'

      // Check if it's a duplicate product error
      if (
        status === 500 && typeof data === 'string' && data.includes('has already been in Wishlist')
      ) {
        message = 'notification.error.productIsAlreadyInWishlist'
      }

      NotificationManager.error({
        message,
      })
    } finally {
      // Always reset loading state, even if component unmounts
      setLoadingIds((previous) => ({
        ...previous,
        [wishlistId]: false,
      }))
    }
  }

  const headerSection = (
    <div className={styles['modal__title-wrapper']}>
      {!isLargerThan992 && (
        <button
          className={`button-as-link ${styles['modal__close-button']}`}
          type="button"
          onClick={handleCancel}
        >
          <ChevronLeftIcon />
        </button>
      )}
      <h1 className={styles.modal__title}>
        {t('wishlist.modal.addProduct.title')}
      </h1>
    </div>
  )

  const wishlistsSection = (
    <>
      {isFetchingWishlists ? skeletons : null}
      {isFetchWishlistsSuccess && isValidArray(wishlists)
        ? (
          <ul className={styles.wishlists}>
            {wishlists.map(
              ({ wishlistId, wishlistName, ownerId, productCount }) => {
                const isSharedWishlist = ownerId !== userId
                return (
                  <li key={wishlistId} className={styles.wishlists__item}>
                    <WishlistItem
                      isShared={isSharedWishlist}
                      loadingIds={loadingIds}
                      productCount={productCount}
                      wishlistId={wishlistId}
                      wishlistName={wishlistName}
                      onClick={async () => handleAdd({ ownerId, wishlistId })}
                    />
                  </li>
                )
              },
            )}
          </ul>
        )
        : null}
    </>
  )

  const newWishlistSection = (
    <>
      <span className={styles['new-wishlist__header']}>
        {t('wishlist.createNewWishlist')}
      </span>
      <NewWishlistNameInput
        placeholder={t('wishlist.placeholder.newWishlistName')}
        value={newWishlistName}
        onChange={handleChangeNewWishlistName}
      />
    </>
  )

  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    [],
  )

  return (
    <>
      <Modal
        centered
        destroyOnClose
        className={styles.modal}
        closable={false}
        footer={null}
        open={isAddProductToWishlistModalVisible}
        width={isLargerThan992 ? 540 : '100%'}
        onCancel={handleCancel}
      >
        {headerSection}
        <ProductToBeAddedToWishlist
          img={img}
          materialName={materialName}
          materialNumber={materialNumber}
          quantity={quantity}
          onChangeQuantity={handleChangeQuantity}
        />
        {wishlistsSection}
        <PermissionWrapper permission={MODULE_WISHLIST_RIGHTS.ADD_WISHLIST}>
          <div className={styles['break-line']} />
          {newWishlistSection}
          <B2becButton
            className={styles['modal__action-button']}
            disabled={!canSave}
            onClick={() => setShouldShowConfirmModal(true)}
          >
            {t('buttonTexts.save')}
          </B2becButton>
        </PermissionWrapper>
      </Modal>
      {shouldShowConfirmModal && (
        <ConfirmationModal
          isExecuting={isExecuting}
          open={shouldShowConfirmModal}
          onCancel={handleCancel}
          onOk={handleSave}
        />
      )}
    </>
  )
}
